import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { AlertTriangle, Globe, Radar } from 'lucide-react';
import { api } from '../api/cliente.js';
import { Botao } from '../componentes/ui/Botao.js';
import { Pill, type TomPill } from '../componentes/ui/Pill.js';

interface Zona {
  zona: string;
  versao: 'V4' | 'V6';
  serial: number | null;
  noPowerDns: boolean;
  bloco: { id: string; cidr: string } | null;
  operadora: { id: string; nome: string } | null;
}

type Delegacao = 'ok' | 'ausente' | 'divergente' | 'nao_verificada';

interface ResultadoDelegacao {
  [zona: string]: { delegacao: Delegacao; nameserversPublicados: string[] };
}

const DELEGACAO: Record<Delegacao, { rotulo: string; tom: TomPill }> = {
  ok: { rotulo: 'Delegada', tom: 'ok' },
  ausente: { rotulo: 'Não delegada', tom: 'erro' },
  divergente: { rotulo: 'Outro servidor', tom: 'aviso' },
  nao_verificada: { rotulo: 'Não verificada', tom: 'neutro' },
};

const FILTROS_ZONA = ['todas', 'v4', 'v6', 'problemas'] as const;
type FiltroZona = (typeof FILTROS_ZONA)[number];

export function Zonas() {
  const [searchParams] = useSearchParams();
  // Aceita ?filtro= vindo do alerta do dashboard ("aguardando delegação").
  const filtroUrl = searchParams.get('filtro');
  const inicial: FiltroZona = FILTROS_ZONA.includes(filtroUrl as FiltroZona)
    ? (filtroUrl as FiltroZona)
    : 'todas';
  const [filtro, setFiltro] = useState<FiltroZona>(inicial);

  const { data: zonas = [], isLoading } = useQuery({
    queryKey: ['zonas'],
    queryFn: () => api.get<Zona[]>('/zonas'),
  });

  const verificar = useMutation({
    mutationFn: () => api.post<ResultadoDelegacao>('/zonas/verificar-delegacao'),
  });

  const delegacoes = verificar.data ?? {};
  const estadoDe = (z: string): Delegacao => delegacoes[z]?.delegacao ?? 'nao_verificada';

  const visiveis = zonas.filter((z) => {
    if (filtro === 'v4') return z.versao === 'V4';
    if (filtro === 'v6') return z.versao === 'V6';
    if (filtro === 'problemas') {
      return !z.noPowerDns || !z.bloco || ['ausente', 'divergente'].includes(estadoDe(z.zona));
    }
    return true;
  });

  const orfas = zonas.filter((z) => z.noPowerDns && !z.bloco).length;
  const faltando = zonas.filter((z) => !z.noPowerDns).length;

  if (isLoading) return <Esqueleto />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {(
          [
            ['todas', 'Todas'],
            ['v4', 'IPv4'],
            ['v6', 'IPv6'],
            ['problemas', 'Com problema'],
          ] as const
        ).map(([chave, rotulo]) => {
          const ativo = filtro === chave;
          return (
            <button
              key={chave}
              onClick={() => setFiltro(chave)}
              className={`h-10 rounded-controle border px-4 text-[13px] font-bold transition-colors ${
                ativo
                  ? 'border-marca bg-tint text-marca'
                  : 'border-borda bg-superficie text-texto-medio hover:border-marca'
              }`}
            >
              {rotulo}
            </button>
          );
        })}
        <Botao
          className="ml-auto"
          iconeEsquerda={<Radar size={15} />}
          onClick={() => verificar.mutate()}
          carregando={verificar.isPending}
          title="Consulta um resolver externo para conferir se as zonas estão delegadas aos nossos nameservers"
        >
          Verificar delegação
        </Botao>
      </div>

      {(orfas > 0 || faltando > 0) && (
        <div className="flex items-start gap-2.5 rounded-card border border-aviso/20 bg-aviso-fundo px-4 py-3 text-[13px] text-aviso">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <p>
            {faltando > 0 && (
              <>
                <strong>{faltando}</strong> zona(s) cadastradas no painel não existem no PowerDNS —
                a criação falhou no meio.{' '}
              </>
            )}
            {orfas > 0 && (
              <>
                <strong>{orfas}</strong> zona(s) existem no PowerDNS sem bloco correspondente —
                sobraram de um bloco removido ou foram criadas por fora.
              </>
            )}
          </p>
        </div>
      )}

      {visiveis.length === 0 ? (
        <div className="rounded-card border border-borda bg-superficie px-6 py-14 text-center">
          <Globe size={24} className="mx-auto mb-2 text-texto-mudo" />
          <p className="text-[14px] font-bold">Nenhuma zona neste filtro</p>
          <p className="mt-1 text-[13px] text-texto-suave">
            As zonas reversas são criadas junto com o bloco de IP.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-card border border-borda bg-superficie">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse">
              <thead>
                <tr className="border-b border-borda bg-borda-tenue">
                  {['Zona', 'Tipo', 'Bloco', 'Operadora', 'Serial (SOA)', 'Delegação'].map((h) => (
                    <th
                      key={h}
                      className="px-[18px] py-3 text-left text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visiveis.map((z) => {
                  const estado = estadoDe(z.zona);
                  const info = DELEGACAO[estado];
                  const ns = delegacoes[z.zona]?.nameserversPublicados ?? [];
                  // A faixa /24 da zona: torna claro que cada linha é um /24
                  // distinto do mesmo bloco, e leva o clique direto a ele.
                  const faixa = ipv4DaZona(z.zona);

                  return (
                    <tr key={z.zona} className="border-t border-borda-suave hover:bg-borda-tenue">
                      <td className="px-[18px] py-[13px]">
                        <span className="font-mono text-[13px] font-semibold">{z.zona}</span>
                        {faixa && (
                          <span className="ml-2 font-mono text-[11.5px] text-texto-mudo">{faixa.cidr}</span>
                        )}
                        {!z.noPowerDns && (
                          <Pill tom="erro" className="ml-2">
                            ausente no PowerDNS
                          </Pill>
                        )}
                      </td>
                      <td className="px-[18px] py-[13px]">
                        <Pill tom={z.versao === 'V6' ? 'ipv6' : 'info'} mono>
                          {z.versao === 'V6' ? 'IPv6' : 'IPv4'}
                        </Pill>
                      </td>
                      <td className="px-[18px] py-[13px]">
                        {z.bloco ? (
                          <Link
                            // Com a faixa /24, leva direto àquele /24 no bloco
                            // (focado), em vez de abrir o /22 inteiro no topo.
                            to={
                              faixa
                                ? `/blocos/${z.bloco.id}/ptr?ip=${faixa.ip}`
                                : `/blocos/${z.bloco.id}/ptr`
                            }
                            className="font-mono text-[12.5px] font-semibold text-marca hover:underline"
                          >
                            {z.bloco.cidr}
                          </Link>
                        ) : (
                          <span className="text-[12.5px] italic text-texto-mudo">órfã</span>
                        )}
                      </td>
                      <td className="px-[18px] py-[13px] text-[13px]">
                        {z.operadora?.nome ?? '—'}
                      </td>
                      <td className="px-[18px] py-[13px] font-mono text-[12.5px]">
                        {z.serial ?? '—'}
                      </td>
                      <td className="px-[18px] py-[13px]">
                        <Pill tom={info.tom}>{info.rotulo}</Pill>
                        {estado === 'divergente' && ns.length > 0 && (
                          // Mostrar para onde está delegada poupa a consulta
                          // manual: normalmente é o provedor anterior.
                          <p className="mt-1 font-mono text-[11px] text-texto-mudo">
                            {ns.slice(0, 2).join(', ')}
                          </p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Faixa /24 (IP inicial e CIDR) de uma zona reversa IPv4 padrão.
 *
 * `30.180.45.in-addr.arpa` → { ip: '45.180.30.0', cidr: '45.180.30.0/24' }. Os
 * octetos vêm invertidos na zona; desinverter dá a rede. Zonas classless (RFC
 * 2317, rótulo com hífen), /16, /8 e IPv6 não casam e devolvem null — aí a tela
 * cai no link do bloco inteiro, sem foco de /24.
 */
function ipv4DaZona(zona: string): { ip: string; cidr: string } | null {
  const m = zona.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.in-addr\.arpa\.?$/);
  if (!m) return null;
  const ip = `${m[3]}.${m[2]}.${m[1]}.0`;
  return { ip, cidr: `${ip}/24` };
}

function Esqueleto() {
  return (
    <div className="rounded-card border border-borda bg-superficie p-4">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="h-12 border-b border-borda-suave last:border-0">
          <div className="h-3 w-1/3 translate-y-4 animate-pulse rounded bg-borda" />
        </div>
      ))}
    </div>
  );
}
